import ReactECharts from "echarts-for-react";
import { useMemo } from "react";
import { baseOption, chartTokens, hexA, tooltipHtml } from "@/lib/chartTheme";
import { prepareLineTuples, type EChartsLinePoint } from "@/lib/chartGaps";
import { useThemeVersion } from "@/hooks/useThemeVersion";
import { formatDateTime } from "@/lib/format";
import type { SiteSnapshot } from "@/data/types";

/** Deviation is "real" above this fraction of the commanded setpoint
 *  (DSGN-08) — below it the band stays neutral grey. */
const DEVIATION_WARN_FRACTION = 0.05;
/** Absolute kW floor for the warn threshold so measurement noise around
 *  a ~0 kW setpoint never paints the band amber. */
const DEVIATION_WARN_FLOOR_KW = 2;
/** Internal warn-overlay series — excluded from legend and tooltip. */
const EXCESS_SERIES = "deviation-above-threshold";

/**
 * The cardinal BESS diagnostic: did the inverter follow the setpoint?
 *
 * Plots commanded power vs measured power on the same axis. When the gap
 * grows beyond the slew rate's expected lag (~5-10s) the operator knows
 * something is off — comm fault, soc limit hit, or sign inversion bug.
 *
 * `bessId` filters per-unit (omit for aggregate).
 */
export function SetpointTracker({
  snapshots,
  bessId,
  height = 220,
}: {
  snapshots: SiteSnapshot[];
  bessId?: string;
  height?: number;
}) {
  // Bumps when [data-theme] flips so chartTokens() are re-read and the
  // option repaints in the new theme's colours (DSGN-07).
  const themeVersion = useThemeVersion();
  const option = useMemo(() => {
    const base = baseOption();
    const t = chartTokens();
    const setpoint: [number, number][] = [];
    const actual: [number, number][] = [];
    for (const s of snapshots) {
      if (bessId) {
        const u = s.bess_per_unit.find((x) => x.id === bessId);
        if (!u) continue;
        setpoint.push([s.ts, u.setpoint_W / 1000]);
        actual.push([s.ts, u.active_W / 1000]);
      } else {
        const sp = s.bess_per_unit.reduce((a, x) => a + x.setpoint_W, 0);
        const ac = s.bess_per_unit.reduce((a, x) => a + x.active_W, 0);
        setpoint.push([s.ts, sp / 1000]);
        actual.push([s.ts, ac / 1000]);
      }
    }
    // Deviation magnitude (kW) — separately plotted on a faint background.
    const deviation: [number, number][] = setpoint.map((p, i) => [p[0], Math.abs(p[1] - (actual[i]?.[1] ?? 0))]);
    // DSGN-08: the band itself is neutral — warn tone is reserved for the
    // stretches where the miss is REAL (> 5 % of the commanded setpoint,
    // with a kW floor). The overlay is derived from the SAME gap-processed
    // series as the grey band, so a real data outage (explicit null from
    // the gap policy) breaks the amber exactly where the band breaks —
    // amber can never bridge an outage with a straight invented segment.
    // Below-threshold points become explicit nulls so the warn overlay
    // only draws over genuine excursions; each run keeps its immediate
    // neighbours so a single-sample excursion still renders as a visible
    // segment instead of an invisible lone point.
    const band = prepareLineTuples(deviation);
    // Threshold needs the commanded setpoint at each (possibly synthetic)
    // band point — setpoint is a step signal, so walk to the last raw
    // sample at or before each ts (band points are ascending).
    let spIdx = 0;
    const exceeds = band.map(([ts, v]) => {
      while (spIdx + 1 < setpoint.length && setpoint[spIdx + 1][0] <= ts) spIdx++;
      if (v == null || setpoint.length === 0) return false;
      return v > Math.max(Math.abs(setpoint[spIdx][1]) * DEVIATION_WARN_FRACTION, DEVIATION_WARN_FLOOR_KW);
    });
    const deviationExcess: EChartsLinePoint[] = band.map(([ts, v], i) =>
      [ts, v != null && (exceeds[i] || exceeds[i - 1] || exceeds[i + 1]) ? v : null]);
    return {
      ...base,
      grid: { ...base.grid, top: 28, left: 50, right: 60, bottom: 28 },
      xAxis: { ...(base.xAxis as any), type: "time" },
      yAxis: [
        { type: "value", axisLabel: { color: t.textMuted, fontSize: 11, formatter: (v: number) => `${v.toFixed(0)} kW` }, splitLine: { lineStyle: { color: t.grid, type: "dashed" } } },
        { type: "value", position: "right", min: 0, axisLabel: { color: t.textMuted, fontSize: 11, formatter: (v: number) => `${v.toFixed(1)} kW` }, splitLine: { show: false } },
      ],
      legend: { ...(base.legend as any), data: ["Setpoint", "Measured", "|Deviation|"] },
      series: [
        {
          name: "Setpoint", type: "line", step: "end", showSymbol: false,
          // Setpoint is a step signal — carry the last commanded value
          // forward across gaps instead of linearly interpolating.
          connectNulls: false,
          data: prepareLineTuples(setpoint, { step: true }),
          // Neutral ink, NOT warn — the commanded value is healthy data,
          // and amber is reserved for degraded states (DSGN-08).
          lineStyle: { color: t.textMuted, width: 2, type: "dashed" },
          itemStyle: { color: t.textMuted },
        },
        {
          name: "Measured", type: "line", showSymbol: false, smooth: 0.25,
          connectNulls: false,
          data: prepareLineTuples(actual),
          lineStyle: { color: t.accent, width: 2 },
          areaStyle: { color: { type: "linear", x: 0, y: 0, x2: 0, y2: 1, colorStops: [
            { offset: 0, color: hexA(t.accent, 0.18) },
            { offset: 1, color: hexA(t.accent, 0) },
          ]}},
        },
        {
          // Neutral grey band — red/amber must not paint a healthy ~0
          // deviation (DSGN-08).
          name: "|Deviation|", type: "line", yAxisIndex: 1, showSymbol: false, smooth: 0.4,
          connectNulls: false,
          data: band,
          lineStyle: { color: t.textFaint, width: 1, opacity: 0.7 },
          itemStyle: { color: t.textFaint },
          areaStyle: { color: t.textFaint, opacity: 0.10 },
        },
        {
          // Warn overlay — the band's own gap-processed values, drawn ONLY
          // where the threshold is exceeded (nulls elsewhere, and the gap
          // policy's outage nulls carry through) so the amber never spreads
          // over healthy stretches or bridges a data outage.
          name: EXCESS_SERIES, type: "line", yAxisIndex: 1, showSymbol: false, smooth: 0.4,
          connectNulls: false,
          silent: true,
          data: deviationExcess,
          lineStyle: { color: t.warn, width: 1.2 },
          itemStyle: { color: t.warn },
          areaStyle: { color: t.warn, opacity: 0.14 },
        },
      ],
      tooltip: {
        ...(base.tooltip as object),
        // All series here are kW — render value+unit rows via the shared
        // tooltipHtml() card (DSGN-09); the warn overlay duplicates the
        // |Deviation| value, so it's filtered out.
        formatter: (params: Array<{ axisValue: number; seriesName: string; color: string; value: [number, number | null] }>) => {
          const list = (Array.isArray(params) ? params : [params])
            .filter((p) => p.seriesName !== EXCESS_SERIES);
          if (!list.length) return "";
          return tooltipHtml({
            header: formatDateTime(Number(list[0].axisValue)),
            rows: list.map((p) => {
              const raw = Array.isArray(p.value) ? p.value[1] : null;
              const ok = typeof raw === "number" && Number.isFinite(raw);
              return {
                color: String(p.color),
                label: p.seriesName,
                value: ok ? raw.toFixed(2) : "—",
                unit: ok ? "kW" : undefined,
              };
            }),
          });
        },
      },
    } as any;
  }, [snapshots, bessId, themeVersion]);
  return <ReactECharts option={option} style={{ height }} notMerge lazyUpdate />;
}

/** Compute the rolling deviation summary used by the diagnostics tile. */
export function deviationSummary(snapshots: SiteSnapshot[], bessId?: string) {
  if (!snapshots.length) return { mean_kW: 0, max_kW: 0, max_pct: 0 };
  let sumAbs = 0;
  let maxAbs = 0;
  let maxPct = 0;
  for (const s of snapshots) {
    const u = bessId ? s.bess_per_unit.find((x) => x.id === bessId) : null;
    const sp = (u ? u.setpoint_W : s.bess_per_unit.reduce((a, x) => a + x.setpoint_W, 0)) / 1000;
    const ac = (u ? u.active_W : s.bess_per_unit.reduce((a, x) => a + x.active_W, 0)) / 1000;
    const d = Math.abs(sp - ac);
    sumAbs += d;
    if (d > maxAbs) maxAbs = d;
    if (Math.abs(sp) > 0.1) maxPct = Math.max(maxPct, (d / Math.abs(sp)) * 100);
  }
  return { mean_kW: sumAbs / snapshots.length, max_kW: maxAbs, max_pct: maxPct };
}
